import { useState } from "react";
import { useI18n } from "@wato/i18n/react";
import { Alert, Button, Row, Stack, useToast } from "@wato/ui";
import { offeredMoneyModules, type ModuleId } from "@wato/business-types";
import { useApi, useErrorMessage } from "../../../api/context";
import { mayUse } from "../../roles/role-label";
import { useWorkspace } from "../../workspace/WorkspaceContext";

const NAME_KEY = {
  accounts: "workspace.modules.prompt.names.accounts",
  quotes: "workspace.modules.prompt.names.quotes",
} as const;
const dismissKey = (ws: string) => `wato.modulesPrompt.dismissed.${ws}`;
function wasDismissed(ws: string): boolean {
  try {
    return window.localStorage.getItem(dismissKey(ws)) === "1";
  } catch {
    return false;
  }
}

/**
 * b82: owner prompt for the money modules (customer accounts, quotes) that the business type's
 * profile lists but that stay off until the owner switches them on (b80). One click calls
 * `PUT .../modules` — an explicit owner action. Staff without `workspace.edit` see nothing.
 * `banner` (home) can be dismissed; `onboarding` sits under the business-type pick.
 */
export function ModulesPrompt({
  variant,
  businessType,
}: {
  readonly variant: "banner" | "onboarding";
  /** Onboarding passes the type picked in the wizard (not saved on the workspace yet). */
  readonly businessType?: string | null | undefined;
}) {
  const { t } = useI18n();
  const api = useApi();
  const toast = useToast();
  const errorMessage = useErrorMessage();
  const { workspace, reload } = useWorkspace();
  const [dismissed, setDismissed] = useState(
    () => variant === "banner" && wasDismissed(workspace.id),
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!mayUse(workspace, "workspace.edit") || !workspace.permissions.manage) return null;
  const offered = offeredMoneyModules(businessType ?? workspace.businessType, workspace.modules);
  if (offered.length === 0 || dismissed) return null;

  const names = offered
    .map((m) => t(NAME_KEY[m as keyof typeof NAME_KEY]))
    .join(t("workspace.modules.prompt.joiner"));

  const turnOn = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.updateWorkspaceModules(
        workspace.id,
        Object.fromEntries(offered.map((m: ModuleId) => [m, true])),
      );
      toast.show({
        tone: "success",
        title: t("workspace.modules.prompt.saved", { modules: names }),
      });
      await reload();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const later = () => {
    setDismissed(true);
    try {
      window.localStorage.setItem(dismissKey(workspace.id), "1");
    } catch {
      /* per-viewer convenience only */
    }
  };

  return (
    <section
      data-testid={`modules-prompt-${variant}`}
      aria-label={t("workspace.modules.prompt.title", { modules: names })}
      className="rounded-lg border border-border bg-surface p-4"
    >
      <Stack gap={2}>
        <h3 className="text-sm font-semibold text-fg">
          {t("workspace.modules.prompt.title", { modules: names })}
        </h3>
        <p className="text-sm text-fg-muted">
          {t("workspace.modules.prompt.body", { modules: names })}
        </p>
        {error && <Alert tone="error">{error}</Alert>}
        <Row gap={2} wrap>
          <Button loading={busy} onClick={() => void turnOn()} data-testid="modules-prompt-turn-on">
            {t("workspace.modules.prompt.cta", { modules: names })}
          </Button>
          {variant === "banner" && (
            <Button variant="ghost" onClick={later} data-testid="modules-prompt-later">
              {t("workspace.modules.prompt.later")}
            </Button>
          )}
        </Row>
      </Stack>
    </section>
  );
}
